import React, { FC } from "react";
import {
    PaginatedGridWrapper,
    GregPrimerTheme,
    Grid,
    GridActionLink,
} from "@getregistered/greg-editors";
import type { PaginatedGridProps, CellRenderFunction, SelectionStatePayload } from "@getregistered/greg-editors/dist/lib/GregGrid/PaginatedGridPrimer/types";
export type { SelectionStatePayload };

import { downloadActionLink, asyncDownloadActionLink } from "../../domain/grid/downloadActionLink";

export interface DownloadLink {
    title: string;
    baseUrl: string;
    // GR-3701: the filters the grid was mounted with, for a grid that opens
    // with a default filter the user can clear. See downloadUrl.
    defaultFilters?: Grid["filters"];
}

export interface AsyncDownloadLink {
    title: string;
    eventId?: string;
    gridType: string;
    exportType: "csv" | "xlsx";
    reportId?: number;
    reportExportsUrl: string;
    extraParams?: Record<string, unknown>;
}

export type BulkActionLink = {
    title: string;
    onClick: (selectionState: SelectionStatePayload) => void;
    dividerBefore?: boolean;
    variant?: "danger";
};

export interface PaginatedApplicationPrimerGridProps extends Omit<PaginatedGridProps, 'grid' | 'loader' | 'defaultPerPage' | 'perPageStorageKey' | 'page' | 'onSelectionChange' | 'RowEditor' | 'onSave' | 'renderCell'> {
    grid: Grid;
    loader?: any;
    defaultPerPage?: number;
    perPageStorageKey?: any;
    page?: number;
    onSelectionChange?: any;
    RowEditor?: any;
    onSave?: any;
    downloadLinks?: DownloadLink[];
    asyncDownloadLinks?: AsyncDownloadLink[];
    actionCellRenderer?: 'menu' | 'buttons';
    showActions?: boolean;
    showMetaCell?: boolean;
    renderCell?: CellRenderFunction;
    onSaveCell?: (row: any, column: any, value?: string) => Promise<void> | void;
    enableRowSelection?: boolean;
    bulkActionLinks?: BulkActionLink[];
    // GR-3342: the grid's Actions menu is the only home for bulk action links,
    // and greg-editors renders that menu only when actionLinks is defined. A
    // grid with no download links (the events index) therefore had row
    // selection and no way to act on it. Set while a selection exists so the
    // menu appears for the bulk links and stays away the rest of the time.
    showBulkActionMenu?: boolean;
}

export const PaginatedApplicationPrimerGrid: FC<PaginatedApplicationPrimerGridProps> = (allProps) => {
    const {
        downloadLinks,
        asyncDownloadLinks,
        actionCellRenderer = 'menu',
        showActions = false,
        showMetaCell = false,
        renderCell,
        onSaveCell,
        enableRowSelection,
        bulkActionLinks,
        showBulkActionMenu = false,
        ...rest
    } = allProps;
    const props = rest as PaginatedGridProps;

    // Build action links from both sync and async download links
    const actionLinks: GridActionLink[] = [];

    // Add sync download links (legacy, direct download)
    if (downloadLinks) {
        downloadLinks.forEach(({ title, baseUrl, defaultFilters }) => {
            actionLinks.push(downloadActionLink(title, baseUrl, defaultFilters));
        });
    }

    // Add async download links (queued via background job)
    if (asyncDownloadLinks) {
        asyncDownloadLinks.forEach(({ title, eventId, gridType, exportType, reportId, reportExportsUrl, extraParams }) => {
            actionLinks.push(asyncDownloadActionLink(title, eventId, gridType, exportType, reportId, reportExportsUrl, extraParams));
        });
    }

    return (
        <GregPrimerTheme>
            <PaginatedGridWrapper
                {...props}
                actionLinks={actionLinks.length > 0 ? actionLinks : (showBulkActionMenu ? [] : undefined)}
                actionCellRenderer={actionCellRenderer}
                showActions={showActions}
                showMetaCell={showMetaCell}
                renderCell={renderCell as any}
                onSaveCell={onSaveCell}
                enableRowSelection={enableRowSelection}
                bulkActionLinks={bulkActionLinks}
            />
        </GregPrimerTheme>
    );
};